import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Card,
  CardBody,
  Text,
  Badge,
  useColorModeValue,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  useToast,
  Link,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { api } from '../services/api';

interface BookFile {
  id: number;
  bookId: number;
  typeId: number;
  fileUrl?: string;
  link?: string;
  basePath?: string;
  fileType?: string;
  book?: {
    id: number;
    name: string;
    classId: number;
    price: number;
  };
  bookType?: {
    id: number;
    typeName: string;
    description?: string;
  };
}

const FilesPage = () => {
  const [files, setFiles] = useState<BookFile[]>([]);
  const [books, setBooks] = useState([]);
  const [bookTypes, setBookTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [baseUrl, setBaseUrl] = useState('http://localhost:3000');
  
  const [formData, setFormData] = useState({
    bookId: '',
    typeId: '',
    link: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [copySuccess, setCopySuccess] = useState('');
  const [fileToDelete, setFileToDelete] = useState<BookFile | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isDeleteModalOpen, 
    onOpen: onDeleteModalOpen, 
    onClose: onDeleteModalClose 
  } = useDisclosure();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');

  // Filter files based on search term
  const filteredFiles = useMemo(() => {
    // Ensure files is an array
    if (!Array.isArray(files)) {
      console.error('Files is not an array:', files);
      return [];
    }
    
    if (!searchTerm) return files;
    
    return files.filter((file) =>
      (file.book?.name && file.book.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (file.book?.classId && file.book.classId.toString().includes(searchTerm)) ||
      (file.bookType?.typeName && file.bookType.typeName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (file.link && file.link.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (file.basePath && file.basePath.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [files, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [filesResponse, booksData, bookTypesData] = await Promise.all([
        api.getBookFiles(),
        api.getBooks(),
        api.getBookTypes(),
      ]);

      // Handle the new API response structure
      if (filesResponse && filesResponse.success && Array.isArray(filesResponse.data)) {
        setFiles(filesResponse.data);
        setBaseUrl(filesResponse.baseUrl || 'http://localhost:3000');
      } else {
        console.error('Invalid files response structure:', filesResponse);
        setFiles([]);
      }

      setBooks(booksData);
      setBookTypes(bookTypesData);
    } catch (error) {
      console.error('Error loading data:', error);
      setFiles([]); // Set empty array on error
      toast({
        title: 'Error',
        description: 'Failed to load data',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile && !formData.link) {
      toast({
        title: 'Error',
        description: 'Please select a file or provide a link',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const uploadData = new FormData();
      uploadData.append('bookId', formData.bookId);
      uploadData.append('typeId', formData.typeId);
      
      if (selectedFile) {
        uploadData.append('file', selectedFile);
      }
      
      if (formData.link) {
        uploadData.append('link', formData.link);
      }

      await api.uploadFile(uploadData);
      toast({
        title: 'Success',
        description: 'File uploaded successfully',
        status: 'success',
        duration: 3000,
      });
      
      resetForm();
      onClose();
      loadData();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload file',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (file: BookFile) => {
    setFileToDelete(file);
    onDeleteModalOpen();
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;

    try {
      await api.deleteBookFile(fileToDelete.id);
      toast({
        title: 'Success',
        description: 'File deleted successfully',
        status: 'success',
        duration: 3000,
      });
      loadData();
      onDeleteModalClose();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete file',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const resetForm = () => {
    setFormData({ bookId: '', typeId: '', link: '' });
    setSelectedFile(null);
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess('Copied!');
      toast({
        title: 'Success',
        description: 'Link copied to clipboard',
        status: 'success',
        duration: 2000,
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        status: 'error',
        duration: 2000,
      });
    }
  };

  const handleDownload = async (file: BookFile) => {
    const url = file.link || `${baseUrl}/${file.basePath}`;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.basePath ? file.basePath.split('/').pop() || 'download' : 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to download file',
        status: 'error',
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading size="xl" color="gray.700">
          ⬆️ Files Management
        </Heading>
        <Button colorScheme="cyan" onClick={() => { resetForm(); onOpen(); }}>
          ➕ Upload File
        </Button>
      </HStack>

      {/* Search Bar */}
      <Card bg={cardBg} shadow="sm">
        <CardBody>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Text>🔍</Text>
            </InputLeftElement>
            <Input
              placeholder="Search files by book name, class, subject, file type, or URL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
            />
          </InputGroup>
          {searchTerm && (
            <Text fontSize="sm" color="gray.500" mt={2}>
              Found {filteredFiles.length} of {files.length} files
            </Text>
          )}
        </CardBody>
      </Card>

      {/* Files Table */}
      <Card bg={cardBg} shadow="md">
        <CardBody>
          {loading ? (
            <Text>Loading...</Text>
          ) : filteredFiles.length === 0 ? (
            searchTerm ? (
              <Alert status="info">
                <AlertIcon />
                No files found matching "{searchTerm}". Try a different search term.
              </Alert>
            ) : (
              <Alert status="info">
                <AlertIcon />
                No files found. Upload your first file!
              </Alert>
            )
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Book</Th>
                  <Th>Class</Th>
                  <Th>File Type</Th>
                  <Th>File/Link</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredFiles.map((file) => (
                  <Tr key={file.id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{file.book?.name}</Text>
                        <Text fontSize="sm" color="gray.500">
                          Class ID: {file.book?.classId}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        Class {file.book?.classId}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme="teal">
                        {file.bookType?.typeName}
                      </Badge>
                    </Td>
                    <Td maxW="300px">
                      {file.link ? (
                        <Link href={file.link} isExternal color="blue.500">
                          {file.link}
                        </Link>
                      ) : file.basePath ? (
                        <Link 
                          href={`${baseUrl}/${file.basePath}`} 
                          isExternal 
                          color="blue.500"
                        >
                          {file.basePath}
                        </Link>
                      ) : (
                        <Text isTruncated>No file path available</Text>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        {(file.link || file.basePath) && (
                          <>
                            <IconButton
                              icon={<Text>🔗</Text>}
                              aria-label="Open link"
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => window.open(
                                file.link || 
                                `${baseUrl}/${file.basePath}`, 
                                '_blank'
                              )}
                            />
                            <IconButton
                              icon={<Text>⬇️</Text>}
                              aria-label="Download file"
                              size="sm"
                              colorScheme="green"
                              variant="ghost"
                              onClick={() => handleDownload(file)}
                            />
                            <IconButton
                              icon={<Text>📋</Text>}
                              aria-label="Copy link"
                              size="sm"
                              colorScheme="purple"
                              variant="ghost"
                              onClick={() => handleCopyLink(file.link || `${baseUrl}/${file.basePath}`)}
                            />
                          </>
                        )}
                        <IconButton
                          icon={<Text>🗑️</Text>}
                          aria-label="Delete"
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDeleteClick(file)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Upload Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload File</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Book</FormLabel>
                  <Select
                    value={formData.bookId}
                    onChange={(e) => setFormData({ ...formData, bookId: e.target.value })}
                  >
                    <option value="">Choose a book</option>
                    {books.map((book: any) => (
                      <option key={book.id} value={book.id}>
                        {book.name} - {book.classId}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>File Type</FormLabel>
                  <Select
                    value={formData.typeId}
                    onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
                  >
                    <option value="">Choose file type</option>
                    {bookTypes.map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.typeName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Upload File</FormLabel>
                  <Input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    accept=".pdf,.doc,.docx,.epub,.mp3,.mp4"
                    p={1}
                  />
                  {selectedFile && (
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Selected: {selectedFile.name}
                    </Text>
                  )}
                </FormControl>
                <Text textAlign="center">OR</Text>
                <FormControl>
                  <FormLabel>External Link</FormLabel>
                  <Input
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="https://example.com/file.pdf"
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="cyan" isLoading={loading}>
                Upload
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text>⚠️</Text>
              <Text>Confirm Delete</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Are you sure you want to delete this file?
              </Text>
              {fileToDelete && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold">
                      {fileToDelete.book?.name}
                    </Text>
                    <Text fontSize="sm">
                      Type: {fileToDelete.bookType?.typeName}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {fileToDelete.link || fileToDelete.basePath}
                    </Text>
                  </VStack>
                </Alert>
              )}
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Text>This action cannot be undone.</Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleDelete}
              leftIcon={<Text>🗑️</Text>}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default FilesPage; 